// Pantalla Agenda del Portal Cobrador (`/collections/agenda`): el explorer de 3 columnas del mock
// —filtros · tabla · ficha— con los cajones de acción. Los datos son fixture: no hay silo de agenda.
import { useMemo, useState } from 'react';
import { MockBoundary } from '@/shared/components/MockBoundary';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { screenFrameClass } from '@/shared/patterns/ScreenFrame';
import { StatsBar } from '@/shared/patterns/StatsBar';
import { TablePager, useTablePager } from '@/shared/patterns/TablePager';
import { AGENDA_ROWS, AGENDA_TODAY } from './fixtures';
import { agendaStatsItems, formatAgendaMoney } from './format';
import {
    activeFilterCount,
    applyStatFilter,
    computeAgendaMetrics,
    computeAgendaStats,
    filterAgenda,
    sortAgenda,
} from './logic';
import {
    emptyAgendaFilters,
    type AgendaDrawerKey,
    type AgendaFilters,
    type AgendaSort,
    type AgendaStatKey,
} from './types';
import { AgendaDetail } from './components/AgendaDetail';
import { AgendaDrawers } from './components/AgendaDrawers';
import { AgendaFiltersPanel } from './components/AgendaFiltersPanel';
import { AgendaTable } from './components/AgendaTable';

/** La agenda se recorre por prioridad: los incumplidos primero, que es la regla de negocio (M4 §4.3). */
const DEFAULT_SORT: AgendaSort = { key: 'priority', direction: 'asc' };

const TOOLBAR =
    'flex flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground';

const METRIC = 'inline-flex items-baseline gap-1 rounded bg-muted/40 px-1.5 py-0.5 text-[10.5px]';
const METRIC_LABEL = 'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

export function AgendaPage() {
    const [filters, setFilters] = useState<AgendaFilters>(emptyAgendaFilters);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [stat, setStat] = useState<AgendaStatKey>('all');
    const [sort, setSort] = useState<AgendaSort>(DEFAULT_SORT);
    const [pickedCreditId, setPickedCreditId] = useState<string | null>(null);
    const [drawer, setDrawer] = useState<AgendaDrawerKey | null>(null);

    const visibleRows = useMemo(
        () =>
            sortAgenda(
                filterAgenda(
                    applyStatFilter(AGENDA_ROWS, stat, AGENDA_TODAY),
                    filters,
                    AGENDA_TODAY,
                ),
                sort,
                AGENDA_TODAY,
            ),
        [stat, filters, sort],
    );

    const pager = useTablePager(visibleRows);
    const stats = useMemo(() => computeAgendaStats(AGENDA_ROWS, AGENDA_TODAY), []);
    const metrics = useMemo(() => computeAgendaMetrics(AGENDA_ROWS, AGENDA_TODAY), []);

    // Sin elección del cobrador se abre el caso más prioritario, como el mock.
    const selectedCreditId = pickedCreditId ?? visibleRows.at(0)?.creditId ?? null;
    const selectedRow = AGENDA_ROWS.find((row) => row.creditId === selectedCreditId) ?? null;

    return (
        <MockBoundary active label="Mock · sin backend" className={screenFrameClass('fill')}>
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                storageKey="collector-agenda"
                filters={
                    <AgendaFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((prev) => !prev);
                        }}
                    />
                }
                list={
                    <>
                        <StatsBar
                            items={agendaStatsItems(stats)}
                            active={stat}
                            onChange={setStat}
                            className="px-2"
                        />
                        <div className={TOOLBAR}>
                            <span>{visibleRows.length} en agenda</span>
                            <span className={METRIC}>
                                <span className={METRIC_LABEL}>Recuperado hoy</span>
                                <span className="font-bold tabular-nums text-success">
                                    {formatAgendaMoney(metrics.recoveredTodayCents)}
                                </span>
                            </span>
                            <span className={METRIC}>
                                <span className={METRIC_LABEL}>Cure rate</span>
                                <span className="font-bold tabular-nums text-info">
                                    {metrics.cureRatePercent}%
                                </span>
                            </span>
                            <span className={METRIC}>
                                <span className={METRIC_LABEL}>Sin contacto 3d+</span>
                                <span className="font-bold tabular-nums text-warning">
                                    {metrics.noContactThreeDays}
                                </span>
                            </span>
                            {activeFilterCount(filters) === 0 ? null : (
                                <span className="rounded bg-primary/10 px-1.5 py-[1px] text-[10px] font-bold text-primary">
                                    {activeFilterCount(filters)} filtros
                                </span>
                            )}
                            <span className="flex-1" />
                            <TablePager
                                page={pager.page}
                                pageCount={pager.pageCount}
                                setPage={pager.setPage}
                            />
                        </div>
                        <AgendaTable
                            rows={pager.pageRows}
                            today={AGENDA_TODAY}
                            sort={sort}
                            onSortChange={setSort}
                            selectedCreditId={selectedCreditId}
                            onSelect={setPickedCreditId}
                        />
                    </>
                }
                detail={
                    <AgendaDetail row={selectedRow} today={AGENDA_TODAY} onOpenDrawer={setDrawer} />
                }
            />
            <AgendaDrawers open={drawer} onOpenChange={setDrawer} row={selectedRow} />
        </MockBoundary>
    );
}
